import React, { createContext, useContext, useEffect, useReducer, useCallback, useState } from 'react';
import API from '../API';

// Persistence helpers
const STORAGE_KEY = 'pst_auth_v1';
const readPersisted = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
};
const writePersisted = (data) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {} };
const clearPersisted = () => { try { localStorage.removeItem(STORAGE_KEY); } catch {} };

// Initial (empty) state; real initial state (with persisted token) is derived lazily below
const initialState = { token: null, user: null, permissions: [], loading: false, error: null };

// Actions
const types = {
  INIT: 'INIT',
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',
};

function derivePermissions(user) {
  if (!user?.roles) return [];
  const flat = user.roles.flatMap(r => r.permissions || []);
  const names = flat.map(p => p.name).filter(Boolean);
  return Array.from(new Set(names));
}

function reducer(state, action) {
  switch(action.type){
    case types.INIT:
      return { ...state, ...action.payload };
    case types.LOGIN_START:
      return { ...state, loading: true, error: null };
    case types.LOGIN_SUCCESS: {
      const { token, user } = action.payload;
      return { ...state, token, user, permissions: derivePermissions(user), loading: false, error: null };
    }
    case types.LOGIN_ERROR:
      return { ...state, loading: false, error: action.payload };
    case types.SET_USER: {
      const user = action.payload;
      return { ...state, user, permissions: derivePermissions(user) };
    }
    case types.LOGOUT:
      return { ...initialState };
    default:
      return state;
  }
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Lazy initializer ensures persisted token is present on first render (prevents flash redirect)
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    const persisted = readPersisted();
    if (persisted?.token) {
      return { ...initialState, token: persisted.token, user: persisted.user, permissions: derivePermissions(persisted.user) };
    }
    return initialState;
  });

  // If we have a token but no user info yet (e.g. previous session stored only token) fetch user once
  useEffect(() => {
    if (state.token && !state.user) {
      fetchMe(state.token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token]);

  const persist = useCallback((token, user) => writePersisted({ token, user }), []);

  const login = useCallback(async (credentials) => {
    dispatch({ type: types.LOGIN_START });
    try {
      const res = await API.login(credentials);
      const token = res?.data?.access_token;
      const user = res?.data?.user;
      if (!token) throw new Error('No token returned');
      persist(token, user);
      dispatch({ type: types.LOGIN_SUCCESS, payload: { token, user } });
      return { token, user };
    } catch (e) {
      dispatch({ type: types.LOGIN_ERROR, payload: e.message || 'Login failed' });
      throw e;
    }
  }, [persist]);

  const logout = useCallback(() => {
    clearPersisted();
    // clear other app-specific persisted keys
    [
      'user',
      'accessToken',
      'selectedRegionId',
      'selectedCouncilId',
      'selectedProcuringEntity',
      'selectedProject',
      'projects',
      'reportingMonth',
      'reportingYear',
      'reports',
      'selectedReportID',
      // additional UI/helpers persisted across the app
      'activeMenuItem',
      'project_data',
      'detailsPayload',
      'subProjectLayerName',
    ].forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
  // As a final sweep, clear any other localStorage keys for a pristine state
  try { localStorage.clear(); } catch {}
    // Clear sessionStorage as well (defensive; not used broadly but ensures clean slate)
    try { sessionStorage.clear(); } catch {}
    // Broadcast a logout event so other modules can react if needed
    try { window.dispatchEvent(new CustomEvent('pst:auth:logout')); } catch {}
    dispatch({ type: types.LOGOUT });
  }, []);

  const fetchMe = useCallback(async (overrideToken) => {
    try {
      const res = await API.getAuthUser?.();
      if (res?.data) {
        dispatch({ type: types.SET_USER, payload: res.data });
        persist(overrideToken || state.token, res.data);
      }
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [persist, state.token, logout]);

  // Listen for global unauthorized events from axios interceptor
  useEffect(() => {
    let redirected = false;
    const handler = () => {
      if (redirected) return; // debounce multiple 401s
      redirected = true;
      logout();
      try {
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const encoded = encodeURIComponent(currentPath || '/');
        window.location.replace(`/signin?next=${encoded}`);
      } catch {
        window.location.replace('/signin');
      }
    };
    window.addEventListener('pst:auth:unauthorized', handler);
    return () => window.removeEventListener('pst:auth:unauthorized', handler);
  }, [logout]);

  // Hydration splash: show while we have a token but no user and we're fetching profile
  const [hydrating, setHydrating] = useState(() => !!(state.token && !state.user));
  useEffect(() => {
    if (state.token && !state.user) setHydrating(true); else setHydrating(false);
  }, [state.token, state.user]);

  const value = {
    ...state,
    login,
    logout,
    fetchMe,
    hydrating,
    hasPermission: (p) => state.permissions.includes(p),
  };
  return (
    <AuthContext.Provider value={value}>
      {hydrating ? (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.85)', zIndex: 9999, fontFamily: 'sans-serif', flexDirection: 'column'
        }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Loading your session…</div>
          <div className="auth-splash-spinner" style={{ width: 40, height: 40, border: '4px solid #1890ff30', borderTopColor: '#1890ff', borderRadius: '50%', animation: 'pst-spin 0.8s linear infinite' }} />
          <style>{`@keyframes pst-spin {to {transform: rotate(360deg)}}`}</style>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
